/* eslint semi: 1 */
import { defineStore } from 'pinia';
import { proxyException } from '@/utils/communication';
import { formatStateItem } from './storeUtils.js';
import { Dependencies } from '../../globals/utils.js';

const MONITORING_STORE_NAME = 'monitoring';
const PLAYER_API_PATH = '/player';

const formatError = (error) => error.response?.data
  ? error.response.data.message ?? error.message
  : error.message || 'Failed to fetch Players';

/**
 * We have to use this store in multiple screens independently,
 * so we need to make sure that the store name is unique.
 * @param {string} uniqueStoreName
 * @returns
 */
export const playersStore = (uniqueStoreName) => {
  return defineStore(`players-store-${uniqueStoreName}`, {
    state: () => ({
      players: [],
      nextToken: null,
      isFetching: false,
      error: null
    }),
    actions: {
      reset () {
        this.$patch({
          players: [],
          nextToken: null,
          isFetching: true,
          error: null
        });
      },
      updatePlayersList (players) {
        this.players = players;
      },
      async _requestPlayers (options = {}, formatter = formatStateItem) {
        const params = {
          ...options
        };

        const $api = Dependencies.inject('$api');
        const { ok: response, error } = await proxyException(
          () => $api.get(`${CONFIG.API_BASE_URL}${PLAYER_API_PATH}`, { params })
        );

        if (error) {
          // short-circuit the request if there is an error
          this.$patch({
            players: [],
            nextToken: null,
            isFetching: false,
            error: formatError(error)
          });
          console.error('Failed to fetch players:', error);
          return null;
        }

        return {
          players: response.data?.players?.map(formatter) ?? [],
          nextToken: response.data.nextToken ?? null
        };
      },
      /**
       * Load all players based on the provided tags and options.
       * This function fetches all players from the API, handling pagination if necessary.
       * @returns list of Players
       */
      async loadAll (options = {}, formatter = formatStateItem) {
        this.reset();
        this.isFetching = true;
        let response = await this._requestPlayers(options, formatter);
        let players = [];
        if (!response) {
          return [];
        }
        players = response.players;

        while (response.nextToken) {
          response = await this._requestPlayers({ ...options, nextToken: response.nextToken }, formatter);
          if (!response) {
            return [];
          }
          players = players.concat(response.players);
        }

        this.$patch({
          isFetching: false,
          nextToken: null,
          players
        });

        return players;
      }
    }
  });
};

export const monitoringPlayerStore = playersStore(MONITORING_STORE_NAME);
